import { useState } from 'react';
import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { FoodHealthDataSubcategory } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import {
  Sheet,
  SheetContent,
  SheetDescription
} from '@design-system/Sheet';
import { cn } from '@utils/styles';
import { isHexColor } from '@utils/checkHexCode';

interface MyFoodsPageCategoryDetailsProps {
  categoryDetailsInfo: FoodHealthDataSubcategory[];
  'data-testid'?: string;
}

export const MyFoodsPageCategoryDetails = ({
  categoryDetailsInfo,
  'data-testid': testId,
}: MyFoodsPageCategoryDetailsProps) => {
  const [displaySubCategoryInfo, setDisplaySubCategoryInfo] =
    useState<FoodHealthDataSubcategory | null>(null);

  const onClickSubCategory = (subCategory: FoodHealthDataSubcategory | null) => {
    if (subCategory) {
      setDisplaySubCategoryInfo(subCategory);
    } else {
      setDisplaySubCategoryInfo(null);
    }
  };

  const handleCloseSheet = () => {
    setDisplaySubCategoryInfo(null);
  };

  const displaySubCategoryDetails = () => {
    const {
      title,
      description,
      link = [],
      rank_color: rankColor = '',
      rank
    } = displaySubCategoryInfo || {};

    return (
      <Sheet open onOpenChange={(open) => !open && handleCloseSheet()}>
        <SheetContent 
          data-testid={`${testId}-modal`}
          variant="glass" 
          className="pt-20 px-8 pb-8"
        >
          <SheetDescription>
            <div 
              data-testid={`${testId}-modalContent`}
              className="flex flex-col"
            >
              <span 
                data-testid={`${testId}-modalTitle`}
                className="typography-h1 text-primary"
              >
                {title}
              </span>
              <p
                data-testid={`${testId}-modalRank`}
                className={cn(
                  'typography-body text-left pt-2',
                  !isHexColor(rankColor) && rankColor
                )}
                style={isHexColor(rankColor) ? { color: rankColor } : undefined}
              >
                {rank}
              </p>
              {description && (
                <span 
                  data-testid={`${testId}-modalDescription`}
                  className="typography-body text-secondary pt-6"
                >
                  {description}
                </span>
              )}
              {(link?.length > 0) && (
                <div 
                  data-testid={`${testId}-modalResearchSection`}
                  className="pt-8"
                >
                  <span 
                    data-testid={`${testId}-modalResearchTitle`}
                    className="typography-title text-primary"
                  >
                    Supporting research
                  </span>
                  {link.map((resultedLink, index) => (
                    <div 
                      key={resultedLink} 
                      data-testid={`${testId}-modalResearchLink-${index}`}
                      className="mt-6"
                    >
                      <a
                        data-testid={`${testId}-modalResearchUrl-${index}`}
                        href={resultedLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all typography-body text-secondary line-clamp-3"
                      >
                        {link}
                      </a>
                      <hr className="border-stroke-primary mt-4" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SheetDescription>
        </SheetContent>
      </Sheet>
    );
  };

  return (
    <div 
      data-testid={testId}
      className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-2"
    >
      {categoryDetailsInfo?.map((subItem) => {
        return (
          <div 
            key={subItem?.id}
            data-testid={`${testId}-scoreCardContainer-${subItem?.id}`}
          >
            <ScoreCard
              key={subItem?.id}
              data-testid={`${testId}-scoreCard-${subItem?.id}`}
              title={subItem?.title}
              tag={subItem?.rank}
              color={subItem?.rank_color || ''}
              score={Number(subItem?.score)}
              variant="light"
              onClick={() => {
                onClickSubCategory(subItem);
              }}
            />
          </div>
        );
      })}
      {displaySubCategoryInfo && displaySubCategoryDetails()}
    </div>
  );
};
